import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface DashboardData {
  artist: {
    name: string;
  };
  leads: {
    count: number;
    activeCount: number;
  };
  payments: {
    totalRevenue: number;
  };
  user: {
    superuser: boolean;
  };
  /** Present when the dashboard request used a bounded date range (per-day buckets). */
  daily?: {
    date: string;
    revenue: number;
    contacts: number;
    active: number;
  }[];
}

interface DashboardContextType {
  data: DashboardData | null;
  loading: boolean;
  refetch: (dateRange?: string, startDate?: Date, endDate?: Date) => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export function DashboardProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchDashboard = useCallback(async (range?: string, startDate?: Date, endDate?: Date) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.append("start", startDate.toISOString().split('T')[0]);
        params.append("end", endDate.toISOString().split('T')[0]);
      } else if (range) {
        params.append("dateRange", range);
      }
      const url = `/api/dashboard${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await fetch(url);
      if (response.status === 401) {
        navigate("/login");
        return;
      }
      if (response.ok) {
        const dashboardData = await response.json();
        setData(dashboardData);
      } else {
        console.error(response);
        console.error("Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const refetch = useCallback((range?: string, startDate?: Date, endDate?: Date) => {
    fetchDashboard(range, startDate, endDate);
  }, [fetchDashboard]);

  return (
    <DashboardContext.Provider value={{ data, loading, refetch }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard(dateRange?: string, startDate?: Date, endDate?: Date) {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }

  useEffect(() => {
    if (dateRange !== undefined || (startDate && endDate)) {
      context.refetch(dateRange, startDate, endDate);
    }
  }, [dateRange, startDate, endDate, context.refetch]);

  const refetchWithRange = useCallback(() => {
    context.refetch(dateRange, startDate, endDate);
  }, [context.refetch, dateRange, startDate, endDate]);

  return {
    data: context.data,
    loading: context.loading,
    refetch: refetchWithRange,
  };
}

